import { HelpCircle, Loader2, RefreshCw, Send } from 'lucide-react';
import { heeftBruikbaarAntwoord, MAX_HERBEOORDELINGEN } from '../lib/meldingStatus';
import type { DuidelijkheidStaat } from '../hooks/useMeldingDuidelijkheid';

/**
 * Doorvragen met uitleg (besluit Tom 01-10): als de AI de melding niet kan
 * naspelen, toont dit paneel waarom ("Waarom ik meer nodig heb") en maximaal
 * drie gerichte vragen. Antwoorden → opnieuw beoordelen (max. twee keer);
 * overslaan mag, maar dan komt de melding binnen als 'onvolledig'.
 */
export default function DuidelijkheidPaneel({
  staat,
  onAntwoord,
  onOpnieuw,
  onOverslaan,
  bezig,
}: {
  staat: DuidelijkheidStaat;
  onAntwoord: (index: number, antwoord: string) => void;
  onOpnieuw: () => void;
  onOverslaan: () => void;
  /** Het formulier is aan het versturen/beoordelen. */
  bezig: boolean;
}) {
  const { beoordeling, vragen, ronde } = staat;
  if (!beoordeling || beoordeling.duidelijk || vragen.length === 0) return null;
  const bruikbaar = heeftBruikbaarAntwoord(vragen);
  const laatsteRonde = ronde >= MAX_HERBEOORDELINGEN - 1;

  return (
    <div className="rounded-xl border border-warning/40 bg-warning/5 p-3 space-y-3">
      <div className="space-y-1">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
          <HelpCircle className="h-4 w-4 text-warning" />
          Waarom ik meer nodig heb
        </p>
        <p className="text-xs text-foreground-secondary">
          {beoordeling.waarom ?? 'Met deze omschrijving kan ik niet naspelen wat er gebeurde.'}
        </p>
      </div>

      {vragen.map((v, i) => (
        <div key={i} className="space-y-1">
          <p className="text-xs font-medium text-foreground">{v.vraag}</p>
          {v.opties?.length ? (
            <div className="flex flex-wrap gap-1.5">
              {v.opties.map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => onAntwoord(i, v.antwoord === o ? '' : o)}
                  className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                    v.antwoord === o
                      ? 'bg-brand text-white'
                      : 'bg-surface text-foreground-secondary border border-theme-border hover:text-foreground'
                  }`}
                >
                  {o}
                </button>
              ))}
              <input
                type="text"
                value={v.opties.includes(v.antwoord ?? '') ? '' : (v.antwoord ?? '')}
                onChange={(e) => onAntwoord(i, e.target.value)}
                placeholder="Anders, namelijk…"
                className="min-w-[10rem] flex-1 px-2.5 py-1 text-xs bg-surface border border-theme-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
            </div>
          ) : (
            <input
              type="text"
              value={v.antwoord ?? ''}
              onChange={(e) => onAntwoord(i, e.target.value)}
              placeholder="Je antwoord (of laat leeg als je het niet weet)"
              className="w-full px-2.5 py-1.5 text-xs bg-surface border border-theme-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/40"
            />
          )}
        </div>
      ))}

      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <p className="text-[11px] text-foreground-tertiary">
          {laatsteRonde
            ? 'Laatste ronde: daarna stuur ik hem in zoals hij is.'
            : 'Na je antwoorden kijk ik nog één keer of ik het kan naspelen.'}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onOverslaan}
            disabled={bezig}
            title="De melding komt dan binnen als 'Onvolledig' en wacht tot je hem aanvult"
            className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-foreground-tertiary hover:text-foreground disabled:opacity-50"
          >
            Versturen zonder aanvulling
          </button>
          <button
            type="button"
            onClick={onOpnieuw}
            disabled={bezig || !bruikbaar}
            title={bruikbaar ? undefined : 'Beantwoord eerst minstens één vraag'}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-hover transition-colors disabled:opacity-50"
          >
            {bezig ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : laatsteRonde ? (
              <Send className="h-3.5 w-3.5" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            {laatsteRonde ? 'Antwoorden en versturen' : 'Antwoorden en opnieuw beoordelen'}
          </button>
        </div>
      </div>
    </div>
  );
}
